const PERMISSION_ALIASES = {
  PRODUCTS_VIEW: "PRODUCT_VIEW",
  PRODUCTS_EDIT: "PRODUCT_EDIT",
};

const normalizePermission = (permission) =>
  PERMISSION_ALIASES[permission] || permission;

export const ALL_PERMISSIONS = [
  "CAMPAIGN_VIEW",
  "CAMPAIGN_EDIT",
  "USER_VIEW",
  "ANALYTICS_VIEW",
  "REPORT_VIEW",
  "PRODUCT_VIEW",
  "PRODUCT_EDIT",
  "GROUP_VIEW",
  "GROUP_EDIT",
  "EMAIL_TEMPLATE_VIEW",
  "EMAIL_TEMPLATE_EDIT",
  "NOTIFICATION_VIEW",
  "NOTIFICATION_EDIT",
  "RELEASE_DOCUMENT_VIEW",
  "RELEASE_DOCUMENT_EDIT",
  "ADMIN_VIEW",
  "ADMIN_EDIT",
];

export const PERMISSIONS = {
  ANALYTICS_VIEW: "ANALYTICS_VIEW",
  REPORT_VIEW: "REPORT_VIEW",
  PRODUCT_VIEW: "PRODUCT_VIEW",
  PRODUCT_EDIT: "PRODUCT_EDIT",
  USER_VIEW: "USER_VIEW",
  USER_EDIT: "USER_EDIT",
  GROUP_VIEW: "GROUP_VIEW",
  GROUP_EDIT: "GROUP_EDIT",
  NOTIFICATION_VIEW: "NOTIFICATION_VIEW",
  NOTIFICATION_EDIT: "NOTIFICATION_EDIT",
  EMAIL_TEMPLATE_VIEW: "EMAIL_TEMPLATE_VIEW",
  EMAIL_TEMPLATE_EDIT: "EMAIL_TEMPLATE_EDIT",
  RELEASE_DOCUMENT_VIEW: "RELEASE_DOCUMENT_VIEW",
  RELEASE_DOCUMENT_EDIT: "RELEASE_DOCUMENT_EDIT",
  CAMPAIGN_VIEW: "CAMPAIGN_VIEW",
  CAMPAIGN_EDIT: "CAMPAIGN_EDIT",
  ADMIN_VIEW: "ADMIN_VIEW",
  ADMIN_EDIT: "ADMIN_EDIT",
};

export const normalizePermissionList = (permissions) => {
  if (!permissions) {
    return [];
  }

  if (Array.isArray(permissions)) {
    return [...new Set(permissions.filter(Boolean).map(normalizePermission))];
  }

  if (typeof permissions === "string" && permissions.trim()) {
    return normalizePermissionList(permissions.split(","));
  }

  return [];
};

const labelToPermissionCode = (label = "") =>
  String(label).trim().replace(/\s+/g, "_").toUpperCase();

const isGroupedPermissionsFormat = (permissions = []) =>
  Array.isArray(permissions) &&
  permissions.length > 0 &&
  typeof permissions[0] === "object" &&
  (permissions[0].title || permissions[0].permissions);

const extractCheckedPermissionsFromGroups = (permissionGroups = []) =>
  permissionGroups.flatMap((group) =>
    (group.permissions || group.permissionList || group.permission || [])
      .filter((permission) => {
        if (typeof permission === "string") {
          return true;
        }

        if (permission?.checked === undefined) {
          return true;
        }

        return Boolean(permission.checked);
      })
      .map(extractPermissionValue)
      .filter(Boolean),
  );

const extractPermissionValue = (permission) => {
  if (typeof permission === "string") {
    return permission.trim();
  }

  if (permission && typeof permission === "object") {
    if (permission.label) {
      return labelToPermissionCode(permission.label);
    }

    return (
      permission.permission ||
      permission.code ||
      permission.name ||
      permission.value ||
      ""
    );
  }

  return "";
};

const parsePermissionsValue = (value) => {
  if (!value) {
    return [];
  }

  if (Array.isArray(value)) {
    return value.map(extractPermissionValue).filter(Boolean);
  }

  if (typeof value === "string") {
    const trimmedValue = value.trim();

    if (!trimmedValue) {
      return [];
    }

    if (trimmedValue.startsWith("[")) {
      try {
        const parsedValue = JSON.parse(trimmedValue);
        if (Array.isArray(parsedValue)) {
          return parsePermissionsValue(parsedValue);
        }
      } catch {
        // Fall back to comma-separated parsing.
      }
    }

    return trimmedValue
      .split(",")
      .map((permission) => permission.trim())
      .filter(Boolean);
  }

  return [];
};

const flattenPermissionGroups = (permissionGroups = []) => {
  if (!Array.isArray(permissionGroups) || !permissionGroups.length) {
    return [];
  }

  if (typeof permissionGroups[0] === "string") {
    return parsePermissionsValue(permissionGroups);
  }

  if (isGroupedPermissionsFormat(permissionGroups)) {
    return extractCheckedPermissionsFromGroups(permissionGroups);
  }

  return permissionGroups.flatMap((group) =>
    parsePermissionsValue(
      group.permissions || group.permissionList || group.permission || [],
    ),
  );
};

export const getPermissionsFromAdminProfile = (profileState) => {
  const response = profileState?.data;
  const profile = response?.data ?? response ?? {};

  const rawPermissions =
    profile.permissions ??
    profile.persmissions ??
    profile.permissionList ??
    response?.permissions ??
    response?.permissionList ??
    [];

  if (isGroupedPermissionsFormat(rawPermissions)) {
    return normalizePermissionList(
      extractCheckedPermissionsFromGroups(rawPermissions),
    );
  }

  const parsedPermissions = parsePermissionsValue(rawPermissions);

  if (parsedPermissions.length) {
    return normalizePermissionList(parsedPermissions);
  }

  const groupedPermissions = flattenPermissionGroups(profile.permissionGroups);

  return normalizePermissionList(groupedPermissions);
};

export const selectAdminPermissions = (state) =>
  getPermissionsFromAdminProfile(state?.getAdminProfile);

export const SUPER_ADMIN_ROLE = "SUPER_ADMIN";
export const ADMIN_ROLE = "ADMIN";

export const getAdminRoleFromProfile = (profileState) => {
  const response = profileState?.data;
  const profile = response?.data ?? response ?? {};

  return profile.role || "";
};

export const isSuperAdmin = (profileState) =>
  String(getAdminRoleFromProfile(profileState)).toUpperCase() === SUPER_ADMIN_ROLE;

export const isAdminRole = (profileState) =>
  String(getAdminRoleFromProfile(profileState)).toLowerCase() === "admin";

export const shouldUseProductViewMode = (profileState) => {
  if (!isAdminRole(profileState)) {
    return false;
  }

  const permissions = getPermissionsFromAdminProfile(profileState);

  return (
    hasPermission(permissions, PERMISSIONS.PRODUCT_VIEW) &&
    !canEditProducts(permissions, profileState)
  );
};

export const shouldApplyPermissionFilter = (profileState) =>
  profileState?.data != null;

export const hasPermission = (userPermissions, permission) => {
  const normalizedUserPermissions = normalizePermissionList(userPermissions);
  const targetPermission = normalizePermission(permission);

  return normalizedUserPermissions.includes(targetPermission);
};

export const hasAnyPermission = (userPermissions, permissionList = []) =>
  permissionList.some((permission) => hasPermission(userPermissions, permission));

export const canViewProducts = (permissions) =>
  hasAnyPermission(permissions, [
    PERMISSIONS.PRODUCT_VIEW,
    PERMISSIONS.PRODUCT_EDIT,
  ]);

export const canEditProducts = (permissions, profileState = null) =>
  isSuperAdmin(profileState) ||
  hasPermission(permissions, PERMISSIONS.PRODUCT_EDIT);

export const canManageProducts = (permissions, profileState = null) =>
  canEditProducts(permissions, profileState);

export const canAddProducts = (permissions, profileState = null) =>
  isSuperAdmin(profileState) ||
  hasPermission(permissions, PERMISSIONS.PRODUCT_EDIT);

export const canViewCampaigns = (permissions) =>
  hasAnyPermission(permissions, [
    PERMISSIONS.CAMPAIGN_VIEW,
    PERMISSIONS.CAMPAIGN_EDIT,
  ]);

export const canEditCampaigns = (permissions, profileState = null) =>
  isSuperAdmin(profileState) ||
  hasPermission(permissions, PERMISSIONS.CAMPAIGN_EDIT);

export const hasCampaignSidebarAccess = (permissions, profileState = null) => {
  if (isSuperAdmin(profileState)) {
    return true;
  }

  return hasAnyPermission(permissions, [
    PERMISSIONS.CAMPAIGN_VIEW,
    PERMISSIONS.CAMPAIGN_EDIT,
  ]);
};

const AI_CAMPAIGN_EDIT_ONLY_ROUTE_PREFIXES = [
  "/ai-campaigns/campaign-details",
  "/ai-campaigns/add-material",
  "/ai-campaigns/manage-material/restart",
];

export const SIDEBAR_MENU_PERMISSIONS = {
  dashboard: [PERMISSIONS.ANALYTICS_VIEW, PERMISSIONS.REPORT_VIEW],
  products: [PERMISSIONS.PRODUCT_VIEW, PERMISSIONS.PRODUCT_EDIT],
  aiCampaigns: [PERMISSIONS.CAMPAIGN_VIEW],
  users: [PERMISSIONS.USER_VIEW, PERMISSIONS.USER_EDIT],
  groups: [PERMISSIONS.GROUP_VIEW, PERMISSIONS.GROUP_EDIT],
  notifications: [
    PERMISSIONS.NOTIFICATION_VIEW,
    PERMISSIONS.NOTIFICATION_EDIT,
  ],
  emailTemplates: [
    PERMISSIONS.EMAIL_TEMPLATE_VIEW,
    PERMISSIONS.EMAIL_TEMPLATE_EDIT,
  ],
  qceTemplates: [
    PERMISSIONS.EMAIL_TEMPLATE_VIEW,
    PERMISSIONS.EMAIL_TEMPLATE_EDIT,
  ],
  releaseDocuments: [
    PERMISSIONS.RELEASE_DOCUMENT_VIEW,
    PERMISSIONS.RELEASE_DOCUMENT_EDIT,
  ],
  adminManagement: [PERMISSIONS.ADMIN_VIEW, PERMISSIONS.ADMIN_EDIT],
};

const ROUTE_PERMISSIONS = [
  { prefix: "/dashboard", permissions: SIDEBAR_MENU_PERMISSIONS.dashboard },
  { prefix: "/product", permissions: SIDEBAR_MENU_PERMISSIONS.products },
  { prefix: "/edit-product", permissions: [PERMISSIONS.PRODUCT_EDIT] },
  {
    prefix: "/view-product",
    permissions: [PERMISSIONS.PRODUCT_VIEW, PERMISSIONS.PRODUCT_EDIT],
  },
  { prefix: "/dpc-extra", permissions: SIDEBAR_MENU_PERMISSIONS.products },
  { prefix: "/edit-dpc-extra", permissions: SIDEBAR_MENU_PERMISSIONS.products },
  { prefix: "/add-dpc-discount", permissions: SIDEBAR_MENU_PERMISSIONS.products },
  { prefix: "/add-dpc", permissions: SIDEBAR_MENU_PERMISSIONS.products },
  {
    prefix: "/insurance-certification",
    permissions: [
      PERMISSIONS.PRODUCT_VIEW,
      PERMISSIONS.PRODUCT_EDIT,
      PERMISSIONS.GROUP_VIEW,
      PERMISSIONS.GROUP_EDIT,
    ],
  },
  { prefix: "/Add-claim", permissions: [PERMISSIONS.PRODUCT_EDIT] },
  {
    prefix: "/view-claim",
    permissions: [PERMISSIONS.PRODUCT_VIEW, PERMISSIONS.PRODUCT_EDIT],
  },
  { prefix: "/claim", permissions: SIDEBAR_MENU_PERMISSIONS.products },
  { prefix: "/user", permissions: SIDEBAR_MENU_PERMISSIONS.users },
  { prefix: "/groups", permissions: SIDEBAR_MENU_PERMISSIONS.groups },
  { prefix: "/notification", permissions: SIDEBAR_MENU_PERMISSIONS.notifications },
  {
    prefix: "/email-templates",
    permissions: SIDEBAR_MENU_PERMISSIONS.emailTemplates,
  },
  { prefix: "/qce-templates", permissions: SIDEBAR_MENU_PERMISSIONS.qceTemplates },
  {
    prefix: "/manage-qce-templates",
    permissions: SIDEBAR_MENU_PERMISSIONS.qceTemplates,
  },
  {
    prefix: "/release-documents",
    permissions: SIDEBAR_MENU_PERMISSIONS.releaseDocuments,
  },
  { prefix: "/admins", permissions: SIDEBAR_MENU_PERMISSIONS.adminManagement },
  { prefix: "/ai-campaigns", permissions: SIDEBAR_MENU_PERMISSIONS.aiCampaigns },
];

const matchesRoutePrefix = (pathname, prefix) =>
  pathname === prefix || pathname.startsWith(`${prefix}/`);

export const HEALTHCARE_BRAND_ID = 1;
export const PRIMECARE_BRAND_ID = 2;

export const isHealthcareBrand = (brandId) =>
  Number(brandId) === HEALTHCARE_BRAND_ID;

export const isPrimeCareBrand = (brandId) => Number(brandId) === PRIMECARE_BRAND_ID;

export const isHealthcareAiCampaignComingSoon = (brandId = null) =>
  isHealthcareBrand(brandId);

export const isHealthcareFeatureComingSoon = (profileState, brandId = null) =>
  isHealthcareBrand(brandId) && Boolean(profileState?.comingSoon);

const PRIMECARE_ONLY_ROUTE_PREFIXES = [
  "/qce-templates",
  "/manage-qce-templates",
];

export const isPrimeCareCampaignListPath = (pathname = "") =>
  pathname === "/ai-campaigns/campaign-list" || pathname === "/ai-campaigns";

export const isPrimeCareOnlyPath = (pathname = "") =>
  PRIMECARE_ONLY_ROUTE_PREFIXES.some((prefix) =>
    matchesRoutePrefix(pathname, prefix),
  ) || isPrimeCareCampaignListPath(pathname);

const isPrimeCareOnlyMenuKey = (menuKey) => menuKey === "qceTemplates";

export const getRouteRequiredPermissions = (pathname = "", brandId = null) => {
  if (isPrimeCareOnlyPath(pathname) && !isPrimeCareBrand(brandId)) {
    return null;
  }
  const matchedRoute = ROUTE_PERMISSIONS.find((route) =>
    matchesRoutePrefix(pathname, route.prefix),
  );

  return matchedRoute?.permissions ?? null;
};

export const canAccessRoute = (
  pathname,
  permissions,
  profileState = null,
  brandId = null,
) => {
  if (isSuperAdmin(profileState)) {
    return true;
  }

  if (isPrimeCareOnlyPath(pathname) && !isPrimeCareBrand(brandId)) {
    return false;
  }

  if (matchesRoutePrefix(pathname, "/addproduct")) {
    return canAddProducts(permissions, profileState);
  }

  if (matchesRoutePrefix(pathname, "/view-product")) {
    return (
      shouldUseProductViewMode(profileState) ||
      isSuperAdmin(profileState) ||
      canViewProducts(permissions)
    );
  }

  if (matchesRoutePrefix(pathname, "/edit-product")) {
    if (shouldUseProductViewMode(profileState)) {
      return false;
    }

    return (
      isSuperAdmin(profileState) ||
      hasPermission(permissions, PERMISSIONS.PRODUCT_EDIT)
    );
  }

  if (matchesRoutePrefix(pathname, "/insurance-certification")) {
    return (
      isSuperAdmin(profileState) ||
      hasPermission(permissions, PERMISSIONS.PRODUCT_VIEW) ||
      hasPermission(permissions, PERMISSIONS.PRODUCT_EDIT) ||
      hasPermission(permissions, PERMISSIONS.GROUP_VIEW) ||
      hasPermission(permissions, PERMISSIONS.GROUP_EDIT)
    );
  }

  if (matchesRoutePrefix(pathname, "/Add-claim")) {
    return (
      isSuperAdmin(profileState) ||
      hasPermission(permissions, PERMISSIONS.PRODUCT_EDIT)
    );
  }

  if (matchesRoutePrefix(pathname, "/view-claim")) {
    return (
      isSuperAdmin(profileState) ||
      hasPermission(permissions, PERMISSIONS.PRODUCT_VIEW) ||
      hasPermission(permissions, PERMISSIONS.PRODUCT_EDIT)
    );
  }

  if (matchesRoutePrefix(pathname, "/ai-campaigns")) {
    const isEditOnlyRoute = AI_CAMPAIGN_EDIT_ONLY_ROUTE_PREFIXES.some((prefix) =>
      matchesRoutePrefix(pathname, prefix),
    );

    if (isHealthcareAiCampaignComingSoon(brandId)) {
      return !isEditOnlyRoute;
    }

    if (isEditOnlyRoute) {
      return canEditCampaigns(permissions, profileState);
    }

    return canViewCampaigns(permissions) || isSuperAdmin(profileState);
  }

  const requiredPermissions = getRouteRequiredPermissions(pathname);

  if (!requiredPermissions) {
    return true;
  }

  return hasAnyPermission(permissions, requiredPermissions);
};

const DEFAULT_ROUTE_ORDER = [
  { menuKey: "dashboard", path: "/dashboard" },
  { menuKey: "products", path: "/product" },
  { menuKey: "aiCampaigns", path: "/ai-campaigns/campaign-list" },
  { menuKey: "users", path: "/user" },
  { menuKey: "groups", path: "/groups" },
  { menuKey: "notifications", path: "/notification" },
  { menuKey: "emailTemplates", path: "/email-templates" },
  { menuKey: "qceTemplates", path: "/qce-templates" },
  { menuKey: "releaseDocuments", path: "/release-documents" },
  { menuKey: "adminManagement", path: "/admins" },
];

export const isProfilePermissionCheckPending = (profileState) =>
  Boolean(
    localStorage.getItem("token") &&
      (profileState?.isLoading ||
        (!shouldApplyPermissionFilter(profileState) &&
          !profileState?.comingSoon)),
  );

export const hasSidebarMenuAccess = (
  menuKey,
  permissions,
  profileState = null,
  brandId = null,
) => {
  if (isSuperAdmin(profileState)) {
    return true;
  }

  if (isPrimeCareOnlyMenuKey(menuKey) && !isPrimeCareBrand(brandId)) {
    return false;
  }

  if (menuKey === "aiCampaigns") {
    if (!isPrimeCareBrand(brandId)) {
      return false;
    }

    return hasCampaignSidebarAccess(permissions, profileState);
  }

  const requiredPermissions = SIDEBAR_MENU_PERMISSIONS[menuKey] ?? [];

  if (!requiredPermissions.length) {
    return true;
  }

  return hasAnyPermission(permissions, requiredPermissions);
};

export const getDefaultRouteForPermissions = (
  permissions,
  profileState = null,
  brandId = null,
) => {
  if (isSuperAdmin(profileState)) {
    return "/dashboard";
  }

  const accessibleRoute = DEFAULT_ROUTE_ORDER.find(
    (route) =>
      hasSidebarMenuAccess(route.menuKey, permissions, profileState, brandId) &&
      canAccessRoute(route.path, permissions, profileState, brandId),
  );

  return accessibleRoute?.path ?? null;
};

export const hasAnyAccessibleRoute = (
  permissions,
  profileState = null,
  brandId = null,
) => Boolean(getDefaultRouteForPermissions(permissions, profileState, brandId));

export const isFriendlyRouteRedirect = (
  pathname,
  authorizedRoute,
  profileState = null,
) => {
  if (
    shouldUseProductViewMode(profileState) &&
    matchesRoutePrefix(pathname, "/edit-product") &&
    authorizedRoute === "/view-product"
  ) {
    return true;
  }

  if (
    matchesRoutePrefix(pathname, "/view-product") &&
    authorizedRoute === "/edit-product"
  ) {
    return true;
  }

  if (
    matchesRoutePrefix(pathname, "/view-claim") &&
    authorizedRoute === "/claim"
  ) {
    return true;
  }

  return false;
};

export const resolveAuthorizedRoute = (
  pathname,
  permissions,
  profileState = null,
  brandId = null,
) => {
  if (isProfilePermissionCheckPending(profileState)) {
    return null;
  }

  const defaultRoute = getDefaultRouteForPermissions(
    permissions,
    profileState,
    brandId,
  );

  if (
    shouldUseProductViewMode(profileState) &&
    matchesRoutePrefix(pathname, "/edit-product")
  ) {
    return "/view-product";
  }

  if (
    matchesRoutePrefix(pathname, "/view-product") &&
    !shouldUseProductViewMode(profileState) &&
    (isSuperAdmin(profileState) ||
      hasPermission(permissions, PERMISSIONS.PRODUCT_EDIT))
  ) {
    return "/edit-product";
  }

  if (
    matchesRoutePrefix(pathname, "/view-claim") &&
    (isSuperAdmin(profileState) ||
      hasPermission(permissions, PERMISSIONS.PRODUCT_EDIT))
  ) {
    return "/claim";
  }

  if (isPrimeCareOnlyPath(pathname) && !isPrimeCareBrand(brandId)) {
    return defaultRoute;
  }

  if (canAccessRoute(pathname, permissions, profileState, brandId)) {
    return pathname;
  }

  return defaultRoute ?? "/";
};

export const canAccessSidebarItem = (
  menuKey,
  permissions,
  profileState = null,
  brandId = null,
) => {
  if (
    !shouldApplyPermissionFilter(profileState) &&
    !isHealthcareFeatureComingSoon(profileState, brandId)
  ) {
    return false;
  }

  return hasSidebarMenuAccess(menuKey, permissions, profileState, brandId);
};

export const isSidebarMenuReady = (profileState) =>
  shouldApplyPermissionFilter(profileState) || Boolean(profileState?.comingSoon);
